import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tour steps for different user roles
const tourSteps = {
  company_admin: [
    {
      target: '[data-testid="dashboard-overview"]',
      title: 'Welcome to Your Dashboard!',
      content: 'This is your control center. View active projects, team members currently working, weekly hours, and pending orders at a glance.',
      position: 'bottom'
    },
    {
      target: '[data-testid="employees-tab"]',
      title: 'Manage Your Team',
      content: 'Add employees, assign roles, manage schedules, and track time. Click on any employee to see their details and manage their account.',
      position: 'bottom'
    },
    {
      target: '[data-testid="projects-tab"]',
      title: 'Project Management',
      content: 'Create and manage projects, assign team members, and track progress. Each project can have its own schedule and team assignments.',
      position: 'bottom'
    },
    {
      target: '[data-testid="schedule-tab"]',
      title: 'Schedule & Assignments',
      content: 'View and create schedules for your team. Assign employees to projects with specific time slots and send daily assignment notifications.',
      position: 'bottom'
    },
    {
      target: '[data-testid="equipment-tab"]',
      title: 'Equipment Pool',
      content: 'Track your equipment inventory. Add equipment, assign to projects, and monitor availability across all your job sites.',
      position: 'bottom'
    },
    {
      target: 'button:has-text("Settings")',
      title: 'Company Settings',
      content: 'Customize your company profile, logo, business hours, and notification preferences. Set up bi-weekly pay periods here.',
      position: 'left'
    }
  ],
  employee: [
    {
      target: '[data-testid="clock-in-btn"], [data-testid="clock-out-btn"]',
      title: 'Clock In/Out',
      content: 'Start and end your work day with a single tap. Your location will be recorded for job site verification.',
      position: 'bottom'
    },
    {
      target: '[data-testid="my-schedule-card"]',
      title: 'Your Schedule',
      content: 'View your upcoming shifts and project assignments. You\'ll receive notifications before your shifts start.',
      position: 'bottom'
    },
    {
      target: '[data-testid="create-order-btn"]',
      title: 'Material Orders',
      content: 'Need supplies? Create a material order request that goes directly to your manager for approval.',
      position: 'bottom'
    }
  ]
};

// Checklist items for getting started
const checklistItems = {
  company_admin: [
    { id: 'profile', label: 'Complete your company profile', action: 'settings' },
    { id: 'employee', label: 'Add your first employee', action: 'add-employee' },
    { id: 'project', label: 'Create a project', action: 'add-project' },
    { id: 'schedule', label: 'Create a schedule', action: 'add-schedule' },
    { id: 'notifications', label: 'Enable push notifications', action: 'notifications' }
  ],
  employee: [
    { id: 'profile', label: 'Complete your personal details', action: 'profile' },
    { id: 'notifications', label: 'Enable push notifications', action: 'notifications' },
    { id: 'clock', label: 'Clock in for your first shift', action: 'clock-in' }
  ]
};

export function OnboardingTour({ user, onComplete }) {
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showChecklist, setShowChecklist] = useState(false);
  const [completedItems, setCompletedItems] = useState([]);

  const userRole = user?.role || 'employee';
  const steps = tourSteps[userRole] || tourSteps.employee;
  const checklist = checklistItems[userRole] || checklistItems.employee;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!user?.id) return;
    
    const checkOnboarding = async () => {
      const localComplete = localStorage.getItem(`onboarding_complete_${user.id}`);
      const hasClosedChecklist = localStorage.getItem(`onboarding_checklist_closed_${user.id}`);
      
      // Check backend for persistent status and login count
      let loginCount = 0;
      let onboardingCompleted = false;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/users/me/onboarding-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          onboardingCompleted = data.onboarding_completed;
          loginCount = data.login_count || 0;
        }
      } catch (e) {}
      
      // If onboarding tour already completed (or synced from backend)
      if (localComplete || onboardingCompleted) {
        if (onboardingCompleted) localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
        // Show checklist only for first 5 logins AND if not closed
        if (loginCount <= 5 && !hasClosedChecklist) {
          setShowChecklist(true);
        }
        const savedItems = localStorage.getItem(`onboarding_checklist_${user.id}`);
        if (savedItems) setCompletedItems(JSON.parse(savedItems));
        return;
      }
      
      // Not completed — show the tour only for first 5 logins
      if (loginCount <= 5) {
        setTimeout(() => setShowTour(true), 1000);
      }
    };
    
    checkOnboarding();
    
    const savedItems = localStorage.getItem(`onboarding_checklist_${user.id}`);
    if (savedItems) setCompletedItems(JSON.parse(savedItems));
  }, [user?.id]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setShowTour(false);
    if (user?.id) {
      localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
      // Persist to backend so it survives cache clears
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/api/users/me/onboarding-complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }
    setShowChecklist(true);
    if (onComplete) onComplete();
  };

  const skipTour = () => {
    setShowTour(false);
    if (user?.id) {
      localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
      // Persist to backend
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/api/users/me/onboarding-complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }
    setShowChecklist(false);
  };

  const closeChecklist = () => {
    setShowChecklist(false);
    if (user?.id) {
      localStorage.setItem(`onboarding_checklist_closed_${user.id}`, 'true');
    }
  };

  const toggleChecklistItem = (itemId) => {
    const newCompleted = completedItems.includes(itemId)
      ? completedItems.filter(id => id !== itemId)
      : [...completedItems, itemId];
    setCompletedItems(newCompleted);
    if (user?.id) {
      localStorage.setItem(`onboarding_checklist_${user.id}`, JSON.stringify(newCompleted));
    }
  };

  const dismissChecklist = () => {
    setShowChecklist(false);
    // Persist the closed state permanently
    if (user?.id) {
      localStorage.setItem(`onboarding_checklist_closed_${user.id}`, 'true');
    }
  };

  const restartTour = () => {
    setCurrentStep(0);
    setShowTour(true);
    setShowChecklist(false);
  };

  // Tour overlay component
  if (showTour) {
    const step = steps[currentStep];
    return (
      <div className="fixed inset-0 z-[100]">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={skipTour} />
        
        {/* Tour Card */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[101]">
          <Card className="w-[400px] shadow-2xl">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-blue-600 font-medium mb-1">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                  <CardTitle className="text-xl">{step.title}</CardTitle>
                </div>
                <Button variant="ghost" size="icon" onClick={skipTour}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-6">{step.content}</p>
              
              {/* Progress dots */}
              <div className="flex justify-center gap-1 mb-4">
                {steps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentStep ? 'bg-blue-600' : idx < currentStep ? 'bg-blue-300' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={skipTour}>
                    Skip Tour
                  </Button>
                  <Button onClick={handleNext}>
                    {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                    {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Checklist widget (floating)
  if (showChecklist && completedItems.length < checklist.length) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Card className="w-[320px] shadow-lg border-2 border-blue-100">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">🚀</span>
                Getting Started
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={dismissChecklist}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-500">
              Complete these steps to get the most out of WorkForce Pro
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {checklist.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                    completedItems.includes(item.id)
                      ? 'bg-green-50 text-green-700'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleChecklistItem(item.id)}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    completedItems.includes(item.id)
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300'
                  }`}>
                    {completedItems.includes(item.id) && (
                      <CheckCircle2 className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <span className={completedItems.includes(item.id) ? 'line-through' : ''}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {completedItems.length}/{checklist.length} completed
              </span>
              <Button variant="link" size="sm" onClick={restartTour}>
                Replay Tour
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

export default OnboardingTour;
